import type { AcceptInvitationResponse, InvitationPreview } from "@xop-any-time/shared";
import { MAX_PASSWORD_LENGTH, checkInvitationPassword } from "@xop-any-time/shared";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useRef, useState, type FormEvent, type JSX } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider.js";
import { ApiError, api } from "../lib/api.js";
import { PASSWORD_HINT, PasswordCoach } from "./accept/PasswordCoach.js";
import { AuthCard, AuthNotice, SubmitButton } from "./login/AuthCard.js";
import { Backdrop } from "./login/Backdrop.js";
import { Field } from "./login/Field.js";
import { EyeIcon, EyeOffIcon } from "./login/icons.js";
import { SiteFooter } from "./login/SiteFooter.js";
import { TopBar } from "./login/TopBar.js";

/**
 * `/accept-invitation?token=…` — where the XOP Reseller invitation email
 * lands, and the first thing a new reseller ever sees of this portal.
 *
 * THIS ROUTE IS THE OTHER HALF OF THE INVITE. Until 2026-08-29 the email's
 * accept link pointed at a route this app did not have: the SPA's catch-all
 * rendered the auth-gated overview, which bounced to /login — and an invitee
 * cannot sign in, because an unaccepted invite leaves `password_set_at` nil
 * and portal-api refuses the account by design. Every reseller invitation
 * was a dead end, however well-branded the email.
 *
 * ── The token is a bearer credential, and it is treated as one ──────────
 *
 * Holding this token is enough to set the account's password, so the page
 * gets it out of the URL before anything else can read it:
 *
 *   1. It is captured ONCE, into a ref, on first render.
 *   2. `history.replaceState` immediately rewrites the address bar to a
 *      bare `/accept-invitation`. It never enters browser history, never
 *      appears in a shared screenshot, and cannot be recovered from the
 *      back button.
 *   3. It is sent to the BFF in a POST BODY, never a URL — a URL is written
 *      to proxy access logs and to the server's own log on any error path.
 *   4. `index.html` sets `referrer: no-referrer`, which covers the instant
 *      between (1) and (2).
 *
 * `replaceState` rather than `pushState`: pushing would leave the
 * token-bearing entry in history, which is the thing being removed.
 *
 * ── The screen ─────────────────────────────────────────────────────────
 *
 * The sign-in card on the sign-in chrome, minus the globe: this is a task
 * someone was sent here to finish, not a landing page, and the two-column
 * hero would push the form below the fold on a phone for no gain.
 *
 * Four states, each a real outcome rather than an error page:
 *
 *   - `loading` — the token is being checked. No form yet: a dead link
 *     should say so before it asks anyone for a password.
 *   - `dead` — expired, spent, unknown, or issued for a different XOP
 *     surface. The BFF decides which sentence; this renders it.
 *   - `ready` — the form, with the invitee's own address shown so they can
 *     see whose link they opened.
 *   - `accepted_not_signed_in` — terminal. The password IS set and the
 *     token IS burned; only the session could not be built.
 */

type Phase =
  | { kind: "loading" }
  | { kind: "dead"; message: string }
  | { kind: "ready"; invite: InvitationPreview }
  | { kind: "accepted_not_signed_in"; message: string };

/**
 * Rewrite the real address bar so the token is no longer in it.
 *
 * Synchronous and called during the first render rather than from an
 * effect: an effect runs after the first paint, and the point is that the
 * URL is already clean by the time anything is on screen.
 *
 * `replaceState`, never `pushState` — pushing would leave the
 * token-bearing entry in history, which is the thing being removed. A
 * no-op when there is no token (or no real `window`, e.g. under a memory
 * router in tests), so it is safe to call unconditionally.
 */
function scrubTokenFromAddressBar(): void {
  if (typeof window === "undefined" || typeof window.history?.replaceState !== "function") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("token")) return;
  url.searchParams.delete("token");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

export function AcceptInvitationPage(): JSX.Element {
  const { user, applicant, refresh } = useAuth();

  // The token is read through the ROUTER, not off `window.location`: the
  // router is what actually owns this app's URL, and reading the global
  // directly would tie the page to one router implementation.
  //
  // Captured into a ref on the first render, then the address bar is
  // scrubbed in the same tick — during render rather than in an effect,
  // because an effect runs after the first paint and the URL should already
  // be clean by then. The ref is what makes the scrub safe: the token
  // survives here after `searchParams` no longer has it.
  const [searchParams] = useSearchParams();
  const tokenRef = useRef<string | null>(null);
  if (tokenRef.current === null) {
    tokenRef.current = searchParams.get("token") ?? "";
    scrubTokenFromAddressBar();
  }
  const token = tokenRef.current;

  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token === "") {
      setPhase({
        kind: "dead",
        message:
          "That link is missing its invitation code. Open the link in your invitation email again, or ask your XOP Networks channel manager for a new one.",
      });
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const invite = await api<InvitationPreview>("/api/auth/invitation", {
          method: "POST",
          body: { token },
        });
        if (!cancelled) setPhase({ kind: "ready", invite });
      } catch (err) {
        if (cancelled) return;
        setPhase({
          kind: "dead",
          message:
            err instanceof ApiError
              ? err.message
              : "We couldn't check that invitation link. Please try again in a moment.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Already signed in — nothing here to do. Checked AFTER the hooks so the
  // hook order never changes between renders; a conditional return above
  // them is the "Rendered more hooks than during the previous render" crash.
  if (user) return <Navigate to="/" replace />;
  if (applicant) return <Navigate to="/onboarding" replace />;

  const email = phase.kind === "ready" ? phase.invite.email : "";

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Both checks run server-side too — that copy is the one that decides.
    // Running them here as well means a mismatch or a weak password costs a
    // keystroke to fix instead of spending the token to find out.
    if (password !== confirmation) {
      setError("Those passwords don't match.");
      return;
    }
    const verdict = checkInvitationPassword(password, email);
    if (!verdict.ok) {
      setError(verdict.message ?? "Choose a stronger password.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await api<AcceptInvitationResponse>("/api/auth/accept-invitation", {
        method: "POST",
        body: { token, password, passwordConfirmation: confirmation },
      });
      if (result.signedIn) {
        // The BFF set an httpOnly cookie the SPA cannot read, so ask who it
        // is rather than adopting the response body as state. The redirect
        // above fires on the next render.
        await refresh();
        return;
      }
      setPhase({
        kind: "accepted_not_signed_in",
        message: result.message ?? "Your password is set. Please sign in.",
      });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "We couldn't set your password just now. Please try again in a moment.",
      );
    } finally {
      // The typed password is deliberately KEPT on a failure. Clearing it
      // would make a transient network error cost someone a long passphrase
      // they have to retype from memory, and the value is already in the
      // field the browser is rendering — dropping it from state protects
      // nothing.
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        overflow: "hidden",
        bgcolor: "background.default",
        color: "text.primary",
        transition: "background-color 200ms, color 200ms",
      }}
    >
      <Backdrop />
      <TopBar />

      <Box
        component="main"
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: 1380,
          mx: "auto",
          px: { xs: 2.5, sm: 5 },
          py: { xs: 5, lg: 7 },
        }}
      >
        {phase.kind === "loading" && (
          <AuthCard title="XOP Reseller" description="Checking your invitation…">
            <Box
              aria-hidden
              sx={{
                mt: 3.5,
                height: 3,
                borderRadius: 999,
                overflow: "hidden",
                bgcolor: (t) => alpha(t.palette.text.primary, 0.1),
                "&::after": {
                  content: '""',
                  display: "block",
                  height: "100%",
                  width: "40%",
                  borderRadius: 999,
                  bgcolor: (t) => t.palette.accent[500],
                  // Transform-only, so it never triggers layout. Stops
                  // entirely under prefers-reduced-motion rather than
                  // slowing down — a looping bar is exactly what that
                  // setting is asking us not to run.
                  animation: "xopIndeterminate 1200ms ease-in-out infinite",
                  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                },
                "@keyframes xopIndeterminate": {
                  "0%": { transform: "translateX(-100%)" },
                  "100%": { transform: "translateX(350%)" },
                },
              }}
            />
          </AuthCard>
        )}

        {phase.kind === "dead" && (
          <AuthCard
            title="This link can't be used"
            description={phase.message}
            footer={
              <>
                Already set a password?{" "}
                <Box
                  component={Link}
                  to="/login"
                  sx={{
                    color: (t) => t.palette.accent[500],
                    fontWeight: 500,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline", textUnderlineOffset: 3 },
                  }}
                >
                  Sign in
                </Box>
                .
              </>
            }
          >
            <Box sx={{ mt: 3 }} />
          </AuthCard>
        )}

        {phase.kind === "accepted_not_signed_in" && (
          <AuthCard title="Your password is set" description={phase.message}>
            <Box
              component={Link}
              to="/login"
              sx={{
                mt: 3.5,
                height: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1.5,
                border: 1,
                borderColor: "divider",
                fontSize: 14,
                fontWeight: 500,
                textDecoration: "none",
                color: "text.primary",
                transition: "background-color 150ms, border-color 150ms",
                "&:hover": {
                  bgcolor: (t) => alpha(t.palette.accent[500], 0.08),
                  borderColor: (t) => alpha(t.palette.accent[500], 0.4),
                },
              }}
            >
              Go to sign-in
            </Box>
          </AuthCard>
        )}

        {phase.kind === "ready" && (
          <AuthCard
            title="Set your password"
            description="One password and you're in. XOP Reseller is the portal for XOP Networks channel partners."
            footer="Trouble finishing? Contact your XOP Networks channel manager."
          >
            <Box
              component="form"
              onSubmit={onSubmit}
              noValidate
              sx={{ mt: 3.25, display: "grid", gap: 2 }}
            >
              {error && <AuthNotice>{error}</AuthNotice>}

              {/* The invited address, shown and not editable: it confirms the
                  invitee opened their own link, and the account is fixed by
                  the token — an editable field would imply it is a choice.
                  The visually-hidden `username` input is what lets a password
                  manager file the new password under the right account; it is
                  aria-hidden and untabbable so it is invisible to everyone
                  except the password manager. */}
              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 1.5,
                  border: 1,
                  borderColor: (t) => alpha(t.palette.accent[500], 0.22),
                  bgcolor: (t) => alpha(t.palette.accent[500], 0.06),
                }}
              >
                <Typography
                  component="p"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "text.secondary",
                  }}
                >
                  Invitation for
                </Typography>
                <Typography
                  component="p"
                  sx={{ mt: 0.25, fontSize: 14.5, fontWeight: 500, wordBreak: "break-all" }}
                >
                  {phase.invite.email}
                </Typography>
              </Box>
              <Box
                component="input"
                type="email"
                value={phase.invite.email}
                autoComplete="username"
                readOnly
                aria-hidden
                tabIndex={-1}
                sx={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: "hidden",
                  clip: "rect(0 0 0 0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }}
              />

              <Box>
                <Field
                  id="password"
                  label="New password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="••••••••••••"
                  value={password}
                  onChange={setPassword}
                  required
                  autoFocus
                  maxLength={MAX_PASSWORD_LENGTH}
                  disabled={submitting}
                  adornment={
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                      disabled={submitting}
                      sx={{
                        display: "inline-flex",
                        height: 32,
                        width: 32,
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 1,
                        border: 0,
                        background: "none",
                        cursor: "pointer",
                        color: "text.disabled",
                        "&:hover": { color: "text.primary" },
                      }}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </Box>
                  }
                />
                {/* One reserved slot for the hint AND the coach. Without a
                    floor the confirm field and the button jump as the
                    guidance rewraps between one and two lines — on every
                    keystroke, which is exactly when someone is looking at
                    the field they are typing in. */}
                <Box sx={{ mt: 1.25, minHeight: 40 }}>
                  {password === "" ? (
                    <Typography
                      component="p"
                      sx={{ fontSize: 12, lineHeight: 1.5, color: "text.secondary" }}
                    >
                      {PASSWORD_HINT}
                    </Typography>
                  ) : (
                    <PasswordCoach password={password} email={email} />
                  )}
                </Box>
              </Box>

              <Field
                id="password-confirmation"
                label="Confirm password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="••••••••••••"
                value={confirmation}
                onChange={setConfirmation}
                required
                maxLength={MAX_PASSWORD_LENGTH}
                disabled={submitting}
                {...(confirmation !== "" && confirmation !== password
                  ? { error: "Doesn't match yet." }
                  : {})}
              />

              <SubmitButton disabled={submitting}>
                {submitting ? "Setting your password…" : "Set password and sign in"}
              </SubmitButton>
            </Box>
          </AuthCard>
        )}
      </Box>

      <SiteFooter />
    </Box>
  );
}
